import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ArrowRight, Star, ChevronLeft, ChevronRight } from 'lucide-react';
// Reusable Carousel Component
function ImageCarousel({
  images,
  altPrefix
}: {
  images: string[];
  altPrefix: string;
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      setCurrentSlide(prev => (prev + 1) % images.length);
    }
    if (touchStart - touchEnd < -50) {
      setCurrentSlide(prev => (prev - 1 + images.length) % images.length);
    }
  };
  return <div className="relative mb-6 rounded-lg overflow-hidden bg-[#0A0D14]" data-id="element-200">
      <div className="relative h-48 md:h-64" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-201">
        <AnimatePresence mode="wait" data-id="element-202">
          <motion.img key={currentSlide} src={images[currentSlide]} alt={`${altPrefix} - imagen ${currentSlide + 1}`} className="absolute inset-0 w-full h-full object-cover" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-203" />
        </AnimatePresence>

        {/* Desktop Arrows */}
        <button onClick={() => setCurrentSlide(prev => (prev - 1 + images.length) % images.length)} className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-[#0A0D14]/80 text-[#CFA86A] hover:bg-[#CFA86A]/20 transition-all z-10" data-id="element-204">
          <ChevronLeft className="w-5 h-5" data-id="element-205" />
        </button>
        <button onClick={() => setCurrentSlide(prev => (prev + 1) % images.length)} className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-[#0A0D14]/80 text-[#CFA86A] hover:bg-[#CFA86A]/20 transition-all z-10" data-id="element-206">
          <ChevronRight className="w-5 h-5" data-id="element-207" />
        </button>
      </div>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 py-3 bg-[#0A0D14]/50 absolute bottom-0 w-full backdrop-blur-[2px]" data-id="element-208">
        {images.map((_, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-[#CFA86A] w-4' : 'bg-[#CFA86A]/30'}`} data-id="element-209" />)}
      </div>
    </div>;
}
export function NewProductSection() {
  const classicImages = ['https://cdn.magicpatterns.com/uploads/hRkgyDhFw81QjkK9Kbi7ZT/WhatsApp_Image_2025-12-04_at_7.35.22_PM.jpg', 'https://cdn.magicpatterns.com/uploads/fWS1JmFxbijXd368cBCGYp/WhatsApp_Image_2025-12-21_at_2.50.26_PM.jpg'];
  const premiumImages = ['https://cdn.magicpatterns.com/uploads/vczePwpdszb12TVhHbw9Ha/WhatsApp_Image_2025-12-21_at_2.46.02_PM.jpg', 'https://cdn.magicpatterns.com/uploads/boPizb66moFWvBeNcaBMkT/WhatsApp_Image_2025-12-21_at_2.50.24_PM.jpg'];
  return <section className="py-16 md:py-24 bg-[#0A0D14] relative overflow-hidden border-t border-[#CFA86A]/10" data-id="element-210">
      {/* Background Ambience */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#CFA86A]/5 via-transparent to-transparent opacity-50" data-id="element-211" />

      <div className="max-w-5xl mx-auto relative z-10 px-4" data-id="element-212">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-8 md:mb-12" data-id="element-213">
          <h2 className="text-3xl md:text-5xl font-bold text-[#F7F5F2] font-serif mb-4" data-id="element-214">
            Compra nuestras cartas del tarot con todos los bonos incluidos.
          </h2>
          <p className="text-[#F7F5F2]/60 text-lg max-w-2xl mx-auto" data-id="element-215">
            Diferentes niveles de calidad según tu necesidad.
          </p>
        </motion.div>
      </div>

      {/* Container that switches from horizontal scroll (mobile) to grid (desktop) */}
      <div className="max-w-5xl mx-auto relative z-10" data-id="element-216">
        <div className="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-2 gap-4 md:gap-6 px-4 pb-8 md:pb-0 hide-scrollbar" data-id="element-217">
          {/* Left Card: Tarot Clásico */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="w-[85vw] md:w-auto flex-shrink-0 snap-center relative bg-[#0C0F17] rounded-2xl border border-[#CFA86A]/20 p-6 md:p-8 flex flex-col" style={{
          boxShadow: '0 0 30px rgba(207, 168, 106, 0.1)'
        }} data-id="element-218">
            <ImageCarousel images={classicImages} altPrefix="Tarot Clásico" data-id="element-219" />

            <h3 className="text-2xl font-bold text-[#F7F5F2] mb-2 font-serif" data-id="element-220">
              Tarot Clásico
            </h3>
            <p className="text-[#F7F5F2]/60 mb-6 text-base" data-id="element-221">
              La compra del Tarot Clásico incluye todos los recursos
              descargables completamente gratis.
            </p>

            <ul className="space-y-3 mb-8 flex-grow" data-id="element-222">
              {['Baraja estándar de tarot', 'Lectura tradicional', 'Uso ocasional', 'Duración estándar'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-223">
                  <CheckCircle2 className="w-5 h-5 text-[#F7F5F2]/40 mt-0.5 shrink-0" data-id="element-224" />
                  <span className="text-[#F7F5F2]/80 text-sm md:text-base leading-tight" data-id="element-225">
                    {item}
                  </span>
                </li>)}
            </ul>

            <a href="https://misticavibes.com/cart/43803698200627:1,43809671184435:1" className="w-full group px-6 py-4 border border-[#CFA86A]/40 text-[#F7F5F2] font-semibold rounded-lg transition-all duration-300 hover:border-[#CFA86A] hover:bg-[#CFA86A]/5 text-base flex items-center justify-center" data-id="element-226">
              <span className="flex items-center justify-center gap-2" data-id="element-227">
                Sólo por hoy $69.99
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-228" />
              </span>
            </a>
          </motion.div>

          {/* Right Card: Tarot Premium */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="w-[85vw] md:w-auto flex-shrink-0 snap-center relative bg-[#0C0F17] rounded-2xl border border-[#CFA86A]/40 p-6 md:p-8 flex flex-col md:scale-105" style={{
          boxShadow: '0 0 50px rgba(207, 168, 106, 0.2)'
        }} data-id="element-229">
            {/* Premium Badge & Discount */}
            <div className="absolute top-4 right-4 md:-top-3 md:right-auto md:left-1/2 md:-translate-x-1/2 z-20 flex flex-col items-end md:items-center gap-1" data-id="element-230">
              <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#CFA86A] text-[#0A0D14] text-xs font-bold tracking-wider shadow-lg" data-id="element-231">
                <Star className="w-3 h-3 fill-current" data-id="element-232" />
                PREMIUM
              </div>
              <div className="flex items-center gap-2 bg-[#0A0D14]/90 px-3 py-1 rounded-full border border-[#CFA86A]/30 backdrop-blur-sm" data-id="element-233">
                <span className="text-red-500 line-through text-xs font-medium" data-id="element-234">
                  $119
                </span>
                <span className="text-[#CFA86A] font-bold text-sm" data-id="element-235">$79.99</span>
              </div>
            </div>

            <ImageCarousel images={premiumImages} altPrefix="Tarot Premium" data-id="element-236" />

            <h3 className="text-2xl font-bold text-[#F7F5F2] mb-2 font-serif" data-id="element-237">
              Tarot Premium
            </h3>
            <p className="text-[#F7F5F2]/60 mb-6 text-base" data-id="element-238">
              La compra del Tarot Premium incluye todos los recursos
              descargables completamente gratis.
            </p>

            <ul className="space-y-3 mb-8 flex-grow" data-id="element-239">
              {['Materiales de alta calidad', 'Cartas coleccionables', 'Alta durabilidad', 'Lectura más detallada'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-240">
                  <CheckCircle2 className="w-5 h-5 text-[#CFA86A] mt-0.5 shrink-0" data-id="element-241" />
                  <span className="text-[#F7F5F2]/90 text-sm md:text-base leading-tight" data-id="element-242">
                    {item}
                  </span>
                </li>)}
            </ul>

            <a href="https://misticavibes.com/cart/43803698200627:1,43814411010099:1" className="w-full group relative px-6 py-4 bg-[#CFA86A] hover:bg-[#b8955a] text-[#0A0D14] font-bold rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(207,168,106,0.3)] hover:shadow-[0_0_40px_rgba(207,168,106,0.5)] overflow-hidden text-base flex items-center justify-center" data-id="element-243">
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-244" />
              <span className="relative flex items-center justify-center gap-2" data-id="element-245">
                Sólo por hoy $79.99
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-246" />
              </span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}